/** Public Terms & Conditions page helpers — parse CMS HTML into accordion sections. */

export const DEFAULT_PLATFORM_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      "These Overall Platform Terms & Conditions govern your access to and use of Trocanet. By creating an account or using any Trocanet service, you agree to these terms.",
    body: `<p>These Overall Platform Terms &amp; Conditions govern your access to and use of Trocanet. By creating an account or using any Trocanet service, you agree to these terms.</p>`,
    icon: "file",
  },
  {
    id: "eligibility-account-registration",
    title: "Eligibility & Account Registration",
    summary:
      "You must be a legally registered business or authorized representative. Accurate registration details are required and accounts may be suspended for false information.",
    body: `<p>You must be a legally registered business or authorized representative. Accurate registration details are required and accounts may be suspended for false information.</p>`,
    icon: "user",
  },
  {
    id: "user-responsibilities",
    title: "User Responsibilities",
    summary:
      "Users must provide accurate information, keep account credentials secure, comply with all applicable laws, and use Trocanet only for legitimate B2B transactions.",
    body: `<p>Users must provide accurate information, keep account credentials secure, comply with all applicable laws, and use Trocanet only for legitimate B2B transactions.</p>`,
    icon: "shield",
  },
  {
    id: "platform-services",
    title: "Platform Services",
    summary:
      "Trocanet provides a B2B marketplace and related tools. Features and availability may change. Trocanet does not guarantee uninterrupted access.",
    body: `<p>Trocanet provides a B2B marketplace and related tools. Features and availability may change. Trocanet does not guarantee uninterrupted access.</p>`,
    icon: "desktop",
  },
  {
    id: "payments-fees",
    title: "Payments & Fees",
    summary:
      "Certain services may include platform or transaction fees. Applicable fees will be disclosed before you confirm a paid action. Fees are generally non-refundable unless stated otherwise.",
    body: `<p>Certain services may include platform or transaction fees. Applicable fees will be disclosed before you confirm a paid action. Fees are generally non-refundable unless stated otherwise.</p>`,
    icon: "card",
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "Users must not post illegal or misleading content, circumvent platform processes, attempt unauthorized access, or use Trocanet for fraudulent activity.",
    body: `<p>Users must not post illegal or misleading content, circumvent platform processes, attempt unauthorized access, or use Trocanet for fraudulent activity.</p>`,
    icon: "ban",
  },
  {
    id: "limitation-of-liability",
    title: "Limitation of Liability",
    summary:
      "Trocanet is provided on an &quot;as is&quot; basis. To the fullest extent permitted by law, Trocanet is not liable for indirect, incidental, or consequential damages arising from use of the platform.",
    body: `<p>Trocanet is provided on an &quot;as is&quot; basis. To the fullest extent permitted by law, Trocanet is not liable for indirect, incidental, or consequential damages arising from use of the platform.</p>`,
    icon: "warning",
  },
  {
    id: "changes-to-terms",
    title: "Changes to Terms",
    summary:
      "Trocanet may update these terms from time to time. Continued use of the platform after changes become effective constitutes acceptance of the updated terms.",
    body: `<p>Trocanet may update these terms from time to time. Continued use of the platform after changes become effective constitutes acceptance of the updated terms.</p>`,
    icon: "lock",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary:
      "For questions about these Terms &amp; Conditions, please contact Trocanet support through the Contact page or support@trocanet.com.",
    body: `<p>For questions about these Terms &amp; Conditions, please contact Trocanet support through the <a href="/contact">Contact</a> page or <a href="mailto:support@trocanet.com">support@trocanet.com</a>.</p>`,
    icon: "envelope",
  },
];

export const DEFAULT_AUCTION_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      "These Auction Module Terms apply when you create, bid on, or manage auctions on Trocanet. They are additional to the Overall Platform Terms.",
    body: `<p>These Auction Module Terms apply when you create, bid on, or manage auctions on Trocanet. They are additional to the Overall Platform Terms &amp; Conditions.</p>`,
    icon: "file",
  },
  {
    id: "eligibility",
    title: "Eligibility",
    summary:
      "Only verified businesses and authorized representatives may participate in Trocanet auctions as sellers or bidders.",
    body: `<p>Only verified businesses and authorized representatives may participate in Trocanet auctions as sellers or bidders. Trocanet may require additional verification before allowing auction participation.</p>`,
    icon: "user",
  },
  {
    id: "auction-rules",
    title: "Auction Rules",
    summary:
      "Auctions must follow listing rules, bidding windows, reserve conditions, and winner confirmation processes published on the platform.",
    body: `<p>Auctions must follow listing rules, bidding windows, reserve conditions, and winner confirmation processes published on the platform. Bids once placed may be binding as described in the auction listing.</p>`,
    icon: "gavel",
  },
  {
    id: "payments-settlement",
    title: "Payments & Settlement",
    summary:
      "Winning bidders and sellers must complete payment and settlement within the timelines stated for each auction.",
    body: `<p>Winning bidders and sellers must complete payment and settlement within the timelines stated for each auction. Failure to settle may result in cancellation, penalties, or account restrictions.</p>`,
    icon: "card",
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "Shill bidding, bid manipulation, fake listings, and offline diversion of auction transactions are strictly prohibited.",
    body: `<p>Shill bidding, bid manipulation, fake listings, and offline diversion of auction transactions are strictly prohibited. Trocanet may suspend participants and cancel auctions that violate these rules.</p>`,
    icon: "ban",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary:
      "For auction-related questions or disputes, contact Trocanet support by email or phone.",
    body: `<p>For auction-related questions or disputes, please contact Trocanet support:</p>
<ul>
<li><strong>Email:</strong> <a href="mailto:support@trocanet.com">support@trocanet.com</a></li>
</ul>`,
    icon: "envelope",
  },
];

export const DEFAULT_INFRASTRUCTURE_LEASING_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      "These Infrastructure Leasing Module Terms apply when you list, search, or lease infrastructure assets on Trocanet.",
    body: `<p>These Infrastructure Leasing Module Terms apply when you list, search, or lease infrastructure assets on Trocanet. They are additional to the Overall Platform Terms &amp; Conditions.</p>`,
    icon: "file",
  },
  {
    id: "eligibility",
    title: "Eligibility",
    summary:
      "Only verified businesses may post infrastructure listings or submit leasing requirements on this module.",
    body: `<p>Only verified businesses and authorized representatives may post infrastructure listings or submit leasing requirements. Trocanet may request ownership, compliance, or capability documents before approving activity.</p>`,
    icon: "user",
  },
  {
    id: "listing-requirement-posting",
    title: "Listing & Requirement Posting",
    summary:
      "Listings and requirements must be accurate, complete, and free from misleading capacity, location, or pricing details.",
    body: `<p>Listings and requirements must be accurate, complete, and free from misleading capacity, location, pricing, or availability details. Users must update or remove outdated posts promptly.</p>`,
    icon: "desktop",
  },
  {
    id: "matching-communication",
    title: "Matching & Communication",
    summary:
      "Trocanet may help connect requesters and providers. Negotiations should remain transparent and compliant with platform rules.",
    body: `<p>Trocanet may help connect requesters and providers through matching tools and messaging. Negotiations should remain transparent, professional, and compliant with platform rules. Offline diversion that circumvents Trocanet protections is not allowed.</p>`,
    icon: "shield",
  },
  {
    id: "payments-charges",
    title: "Payments & Charges",
    summary:
      "Applicable platform, listing, or transaction charges will be disclosed before confirmation of a paid action.",
    body: `<p>Applicable platform, listing, or transaction charges will be disclosed before confirmation of a paid action. Settlement between lessors and lessees must follow the agreed commercial terms and any Trocanet payment guidelines.</p>`,
    icon: "card",
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "Fake listings, unauthorized asset offers, fraudulent documentation, and harmful or illegal leasing activity are prohibited.",
    body: `<p>Fake listings, unauthorized asset offers, fraudulent documentation, misrepresentation of ownership or safety status, and any illegal or harmful leasing activity are prohibited. Trocanet may remove content and restrict accounts for violations.</p>`,
    icon: "ban",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary:
      "For questions about the Infrastructure Leasing Module, contact leasing.support@trocanet.com.",
    body: `<p>For any questions or issues related to the Infrastructure Leasing Module, please contact our support team.</p>
<p><a href="mailto:leasing.support@trocanet.com">leasing.support@trocanet.com</a></p>`,
    icon: "envelope",
  },
];

export const DEFAULT_CONTRACT_MANUFACTURING_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      "Trocanet Contract Manufacturing Module connects businesses with verified contract manufacturers for OEM, ODM, private label, and custom manufacturing projects.",
    body: `<p>Trocanet Contract Manufacturing Module connects businesses with verified contract manufacturers for OEM, ODM, private label, white label, and custom manufacturing projects.</p>
<p>The platform facilitates collaboration but does not become a party to commercial agreements.</p>`,
    icon: "industry",
  },
  {
    id: "eligibility",
    title: "Eligibility",
    summary:
      "Only verified companies may participate. Users must complete KYC and provide valid company details.",
    body: `<p>Only verified companies may participate. Users must:</p>
<ul>
<li>Complete KYC</li>
<li>Provide valid GST and company details</li>
<li>Have authority to manufacture or outsource products</li>
<li>Comply with industry regulations</li>
</ul>
<p>Trocanet reserves the right to suspend accounts providing false information.</p>`,
    icon: "user",
  },
  {
    id: "manufacturing-requirements",
    title: "Manufacturing Requirements",
    summary:
      "Buyers and manufacturers should share complete specifications, capacity, certifications, MOQ, lead time, and pricing details.",
    body: `<p><strong>Buyers should provide:</strong></p>
<ul>
<li>Product Specifications</li>
<li>Drawings</li>
<li>Quantity</li>
<li>Quality Standards</li>
<li>Packaging Requirements</li>
<li>Delivery Timeline</li>
</ul>
<p><strong>Manufacturers should provide:</strong></p>
<ul>
<li>Production Capacity</li>
<li>Certifications</li>
<li>MOQ</li>
<li>Lead Time</li>
<li>Pricing</li>
<li>Manufacturing Capabilities</li>
</ul>
<p>Incomplete information may delay project matching.</p>`,
    icon: "desktop",
  },
  {
    id: "orders-payments",
    title: "Orders & Payments",
    summary:
      "Commercial agreements remain between Buyer and Manufacturer. Trocanet may facilitate RFQs, POs, escrow, and milestone tracking.",
    body: `<p>All commercial agreements remain between Buyer and Manufacturer.</p>
<p>Trocanet may facilitate:</p>
<ul>
<li>RFQ Management</li>
<li>Purchase Orders</li>
<li>Escrow (if enabled)</li>
<li>Milestone Tracking</li>
</ul>
<p>Applicable platform fees are displayed before confirmation.</p>`,
    icon: "card",
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "Fake quotations, misrepresentation, counterfeit products, IP violations, and unauthorized sharing of confidential information are prohibited.",
    body: `<p>Users shall not:</p>
<ul>
<li>Submit fake quotations</li>
<li>Misrepresent manufacturing capabilities</li>
<li>Upload counterfeit products</li>
<li>Violate intellectual property</li>
<li>Share confidential information without authorization</li>
</ul>
<p>Violation may result in listing removal, account suspension, or permanent account termination.</p>`,
    icon: "warning",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary:
      "For Contract Manufacturing Support, contact support@trocanet.com. Business hours: Monday–Saturday, 9 AM–7 PM IST.",
    body: `<p><strong>Contract Manufacturing Support</strong></p>
<p><a href="mailto:support@trocanet.com">support@trocanet.com</a></p>
<p><strong>Business Hours</strong><br />Monday–Saturday<br />9 AM–7 PM IST</p>
<p><a href="/contact">Contact Support</a></p>`,
    icon: "envelope",
  },
];

export const DEFAULT_PROCURE_TO_PAY_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      "Trocanet Procure-to-Pay Engine enables businesses to manage the complete procurement lifecycle digitally.",
    body: `<p>Trocanet Procure-to-Pay Engine enables businesses to manage the complete procurement lifecycle digitally, including purchase requests, supplier quotations, purchase orders, approvals, invoices, payments, and procurement analytics.</p>
<p>The platform acts only as a technology provider and does not become a contracting party between buyers and suppliers.</p>`,
    icon: "desktop",
  },
  {
    id: "user-eligibility-responsibilities",
    title: "User Eligibility & Responsibilities",
    summary:
      "Users must complete verification, provide accurate procurement information, and follow company policies and applicable laws.",
    body: `<p>Users must:</p>
<ul>
<li>Complete company verification</li>
<li>Provide accurate procurement information</li>
<li>Maintain confidential login credentials</li>
<li>Follow company procurement policies</li>
<li>Comply with applicable laws and regulations</li>
</ul>
<p>Unauthorized system usage may result in immediate account suspension.</p>`,
    icon: "user",
  },
  {
    id: "procurement-workflow",
    title: "Procurement Workflow",
    summary:
      "Supports requisitions, RFQs, quotations, purchase orders, approvals, goods receipt, invoices, and payment tracking.",
    body: `<p>The Procure-to-Pay Engine supports:</p>
<ul>
<li>Purchase Requisitions</li>
<li>RFQs</li>
<li>Supplier Quotations</li>
<li>Purchase Orders</li>
<li>Order Approvals</li>
<li>Goods Receipt</li>
<li>Invoice Processing</li>
<li>Payment Tracking</li>
</ul>
<p>Users are responsible for reviewing and approving procurement transactions before submission.</p>`,
    icon: "shield",
  },
  {
    id: "payments-financial-transactions",
    title: "Payments & Financial Transactions",
    summary:
      "Payment obligations remain between buyers and suppliers. Trocanet may facilitate tracking, reconciliation, and invoice status updates.",
    body: `<p>Payment obligations remain solely between buyers and suppliers.</p>
<p>Where applicable, Trocanet may facilitate:</p>
<ul>
<li>Digital payment tracking</li>
<li>Invoice reconciliation</li>
<li>Payment status updates</li>
<li>Tax invoice management</li>
</ul>
<p>Platform service fees and applicable taxes will be displayed before confirmation.</p>`,
    icon: "card",
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "Fraudulent POs, fake invoices, approval manipulation, and workflow circumvention are prohibited.",
    body: `<p>Users shall not:</p>
<ul>
<li>Create fraudulent purchase orders</li>
<li>Upload fake invoices</li>
<li>Manipulate procurement approvals</li>
<li>Circumvent platform workflows</li>
<li>Submit misleading procurement data</li>
</ul>
<p>Violations may result in procurement suspension, account restriction, or permanent termination.</p>`,
    icon: "warning",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary:
      "For Procure-to-Pay Support, contact support@trocanet.com. Business hours: Monday–Saturday, 9:00 AM–7:00 PM IST.",
    body: `<p><strong>Procure-to-Pay Support</strong></p>
<p><a href="mailto:support@trocanet.com">support@trocanet.com</a></p>
<p><strong>Business Hours</strong><br />Monday – Saturday<br />9:00 AM – 7:00 PM IST</p>
<p><a href="/contact">Contact Support</a></p>`,
    icon: "envelope",
  },
];

export const DEFAULT_QUALITY_ASSESSMENT_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      "Trocanet enables businesses to connect with verified quality assessment professionals for inspections, audits, testing, and certifications.",
    body: `<p>Trocanet enables businesses to connect with verified quality assessment professionals for inspections, audits, testing, certifications, supplier evaluations, and quality assurance services.</p>
<p>Trocanet acts solely as a technology platform facilitating professional engagement and does not guarantee certification outcomes.</p>`,
    icon: "shield",
  },
  {
    id: "professional-eligibility",
    title: "Professional Eligibility",
    summary:
      "Quality professionals must maintain valid certifications, accurate qualifications, confidentiality, and regulatory compliance.",
    body: `<p>Quality professionals must:</p>
<ul>
<li>Maintain valid certifications and professional licenses</li>
<li>Provide accurate qualifications and experience</li>
<li>Follow applicable industry standards</li>
<li>Maintain confidentiality of client information</li>
<li>Comply with legal and regulatory requirements</li>
</ul>
<p>Trocanet reserves the right to verify credentials and suspend professionals providing false information.</p>`,
    icon: "user",
  },
  {
    id: "inspection-assessment-services",
    title: "Inspection & Assessment Services",
    summary:
      "Professionals may perform factory and product inspections, audits, laboratory testing, compliance verification, and quality reports.",
    body: `<p>Quality professionals may perform:</p>
<ul>
<li>Factory Inspections</li>
<li>Product Quality Inspections</li>
<li>Supplier Audits</li>
<li>Process Audits</li>
<li>Laboratory Testing</li>
<li>Compliance Verification</li>
<li>Certification Support</li>
<li>Quality Reports</li>
</ul>
<p>Inspection findings should be objective, accurate, and evidence-based.</p>`,
    icon: "desktop",
  },
  {
    id: "reports-confidentiality",
    title: "Reports & Confidentiality",
    summary:
      "Inspection reports remain confidential. Professionals must submit factual reports and protect proprietary information.",
    body: `<p>Inspection reports remain confidential between the requesting business and the assigned professional.</p>
<p>Professionals shall:</p>
<ul>
<li>Submit factual reports</li>
<li>Protect confidential information</li>
<li>Avoid conflicts of interest</li>
<li>Not disclose proprietary business information without authorization</li>
</ul>`,
    icon: "lock",
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "False reports, unauthorized incentives, misrepresentation, and manipulated audit results are prohibited.",
    body: `<p>Professionals shall not:</p>
<ul>
<li>Issue false inspection reports</li>
<li>Accept unauthorized incentives</li>
<li>Misrepresent certifications</li>
<li>Share confidential client information</li>
<li>Manipulate audit results</li>
<li>Perform inspections without proper authorization</li>
</ul>
<p>Violations may result in immediate account suspension, removal from the marketplace, permanent termination, and legal action where applicable.</p>`,
    icon: "warning",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary:
      "For Quality Assessment Support, contact support@trocanet.com. Business hours: Monday–Saturday, 9:00 AM–7:00 PM IST.",
    body: `<p><strong>Quality Assessment Support</strong></p>
<p><a href="mailto:support@trocanet.com">support@trocanet.com</a></p>
<p><strong>Business Hours</strong><br />Monday – Saturday<br />9:00 AM – 7:00 PM IST</p>
<p><a href="/contact">Contact Support</a></p>`,
    icon: "envelope",
  },
];

export const DEFAULT_CONSULTANTS_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      "Trocanet enables businesses to connect with verified consultants for advisory, technical, financial, and project-based professional services.",
    body: `<p>Trocanet enables businesses to connect with verified consultants for strategic advisory, technical consulting, operational improvement, digital transformation, project management, financial consulting, HR consulting, and industry-specific professional services.</p>
<p>Trocanet acts only as a digital platform facilitating professional engagement and is not a party to consulting agreements.</p>`,
    icon: "user",
  },
  {
    id: "consultant-eligibility",
    title: "Consultant Eligibility",
    summary:
      "Consultants must complete KYC, keep accurate profiles, provide genuine qualifications, and follow professional ethics and laws.",
    body: `<p>Consultants must:</p>
<ul>
<li>Complete KYC verification</li>
<li>Maintain accurate professional profiles</li>
<li>Provide genuine qualifications and experience</li>
<li>Comply with professional ethics</li>
<li>Follow applicable laws and regulations</li>
</ul>
<p>Trocanet reserves the right to verify credentials and suspend accounts providing misleading information.</p>`,
    icon: "shield",
  },
  {
    id: "consulting-engagement",
    title: "Consulting Engagement",
    summary:
      "Parties should clearly define scope, deliverables, timelines, commercial terms, confidentiality, and communication channels.",
    body: `<p>Businesses and consultants should clearly define:</p>
<ul>
<li>Project Scope</li>
<li>Deliverables</li>
<li>Timelines</li>
<li>Commercial Terms</li>
<li>Confidentiality Requirements</li>
<li>Communication Channels</li>
</ul>
<p>Both parties are responsible for mutually agreed consulting engagements.</p>`,
    icon: "desktop",
  },
  {
    id: "confidentiality-intellectual-property",
    title: "Confidentiality & Intellectual Property",
    summary:
      "Consultants must protect confidential information and respect IP rights. Deliverable ownership is set by the client agreement.",
    body: `<p>Consultants shall:</p>
<ul>
<li>Protect confidential business information</li>
<li>Use information only for authorized purposes</li>
<li>Respect intellectual property rights</li>
<li>Avoid unauthorized disclosure of client information</li>
</ul>
<p>Ownership of deliverables shall be governed by the agreement between the consultant and the client.</p>`,
    icon: "lock",
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "Misrepresentation, false certifications, confidential leaks, unauthorized payments, and fraudulent practices are prohibited.",
    body: `<p>Consultants shall not:</p>
<ul>
<li>Misrepresent qualifications</li>
<li>Submit false certifications</li>
<li>Share confidential client information</li>
<li>Accept unauthorized payments</li>
<li>Engage in fraudulent consulting practices</li>
<li>Violate applicable professional standards</li>
</ul>
<p>Violations may result in account suspension, removal from the marketplace, permanent account termination, and legal action where applicable.</p>`,
    icon: "warning",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary:
      "For Consulting Services Support, contact support@trocanet.com. Business hours: Monday–Saturday, 9:00 AM–7:00 PM IST.",
    body: `<p><strong>Consulting Services Support</strong></p>
<p><a href="mailto:support@trocanet.com">support@trocanet.com</a></p>
<p><strong>Business Hours</strong><br />Monday – Saturday<br />9:00 AM – 7:00 PM IST</p>
<p><a href="/contact">Contact Support</a></p>`,
    icon: "envelope",
  },
];

export const DEFAULT_LEGAL_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      "Trocanet enables businesses to connect with verified legal professionals for contracts, compliance, IP, and corporate legal services.",
    body: `<p>Trocanet enables businesses to connect with verified legal professionals for commercial agreements, contract drafting, compliance advisory, intellectual property matters, corporate governance, legal documentation, dispute resolution support, and other business legal services.</p>
<p>Trocanet acts solely as a technology platform facilitating connections between businesses and legal professionals and is not a law firm or legal representative.</p>`,
    icon: "gavel",
  },
  {
    id: "professional-eligibility",
    title: "Professional Eligibility",
    summary:
      "Legal professionals must complete KYC, hold valid qualifications, keep accurate profiles, and protect client confidentiality.",
    body: `<p>Legal professionals must:</p>
<ul>
<li>Complete identity and KYC verification</li>
<li>Hold valid professional qualifications and licenses where applicable</li>
<li>Maintain accurate profile information</li>
<li>Follow professional ethics and applicable laws</li>
<li>Protect client confidentiality</li>
</ul>
<p>Trocanet reserves the right to verify credentials and suspend accounts providing inaccurate or misleading information.</p>`,
    icon: "user",
  },
  {
    id: "legal-services-engagement",
    title: "Legal Services Engagement",
    summary:
      "Parties should agree on scope, deliverables, timelines, fees, communication, and confidentiality. Trocanet does not provide legal advice.",
    body: `<p>Businesses and legal professionals should mutually agree on:</p>
<ul>
<li>Scope of legal services</li>
<li>Deliverables</li>
<li>Timelines</li>
<li>Professional fees</li>
<li>Communication methods</li>
<li>Confidentiality obligations</li>
</ul>
<p>Trocanet is not responsible for the legal advice or opinions provided by professionals.</p>`,
    icon: "desktop",
  },
  {
    id: "confidentiality-professional-responsibility",
    title: "Confidentiality & Professional Responsibility",
    summary:
      "Legal professionals must maintain confidentiality, avoid conflicts of interest, and deliver services ethically and independently.",
    body: `<p>Legal professionals shall:</p>
<ul>
<li>Maintain strict confidentiality</li>
<li>Protect client documents and information</li>
<li>Avoid conflicts of interest</li>
<li>Follow applicable legal ethics</li>
<li>Respect intellectual property rights</li>
<li>Deliver services professionally and independently</li>
</ul>`,
    icon: "lock",
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "Misleading information, misrepresentation, confidential leaks, fraud, and misuse of platform data are prohibited.",
    body: `<p>Legal professionals shall not:</p>
<ul>
<li>Provide misleading legal information</li>
<li>Misrepresent qualifications or licenses</li>
<li>Share confidential client information</li>
<li>Engage in fraudulent legal practices</li>
<li>Violate applicable legal or professional regulations</li>
<li>Misuse platform data</li>
</ul>
<p>Violations may result in account suspension, permanent removal, reporting to regulatory authorities where applicable, and legal action.</p>`,
    icon: "warning",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary:
      "For Legal Services Support, contact support@trocanet.com. Business hours: Monday–Saturday, 9:00 AM–7:00 PM IST.",
    body: `<p><strong>Legal Services Support</strong></p>
<p><a href="mailto:support@trocanet.com">support@trocanet.com</a></p>
<p><strong>Business Hours</strong><br />Monday – Saturday<br />9:00 AM – 7:00 PM IST</p>
<p><a href="/contact">Contact Support</a></p>`,
    icon: "envelope",
  },
];

export const DEFAULT_LOGISTICS_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      "Trocanet Logistics connects businesses with verified logistics providers for transportation, warehousing, tracking, and delivery.",
    body: `<p>Trocanet Logistics Module enables businesses to connect with verified logistics providers for domestic and international transportation, freight forwarding, warehousing, shipment tracking, customs coordination, and last-mile delivery services.</p>
<p>Trocanet acts as a digital platform connecting businesses with logistics partners and does not itself transport or store goods.</p>`,
    icon: "truck",
  },
  {
    id: "user-eligibility-responsibilities",
    title: "User Eligibility & Responsibilities",
    summary:
      "Businesses and logistics providers must complete KYC, keep accurate information, and follow transport and customs laws.",
    body: `<p>Businesses and logistics providers must:</p>
<ul>
<li>Complete KYC verification</li>
<li>Maintain accurate company information</li>
<li>Provide genuine shipment details</li>
<li>Follow transportation and customs regulations</li>
<li>Ensure compliance with applicable laws</li>
</ul>
<p>Trocanet reserves the right to suspend accounts providing false or misleading information.</p>`,
    icon: "user",
  },
  {
    id: "shipment-booking-transportation",
    title: "Shipment Booking & Transportation",
    summary:
      "Users may book road, air, sea, rail, warehousing, customs support, tracking, and last-mile delivery. Accurate shipment details are required.",
    body: `<p>Users may request logistics services including:</p>
<ul>
<li>Road Transport</li>
<li>Air Freight</li>
<li>Sea Freight</li>
<li>Rail Transport</li>
<li>Warehousing</li>
<li>Customs Clearance Support</li>
<li>Shipment Tracking</li>
<li>Last-Mile Delivery</li>
</ul>
<p>Businesses are responsible for providing accurate shipment information, including weight, dimensions, packaging, hazardous material declarations (if applicable), pickup locations, and delivery destinations.</p>`,
    icon: "industry",
  },
  {
    id: "freight-charges-payments",
    title: "Freight Charges & Payments",
    summary:
      "Freight charges are agreed between parties. Trocanet may facilitate quotations, booking, invoices, payment tracking, and service fees.",
    body: `<p>Freight charges are agreed between businesses and logistics providers.</p>
<p>Where applicable, Trocanet may facilitate:</p>
<ul>
<li>Freight quotations</li>
<li>Booking confirmation</li>
<li>Invoice management</li>
<li>Payment tracking</li>
<li>Service fee collection</li>
</ul>
<p>All applicable taxes and platform service charges will be displayed before booking confirmation.</p>`,
    icon: "card",
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "False shipment data, illegal goods, tracking manipulation, fraud, and customs violations are prohibited.",
    body: `<p>Users shall not:</p>
<ul>
<li>Submit false shipment information</li>
<li>Transport prohibited or illegal goods</li>
<li>Manipulate shipment tracking data</li>
<li>Misrepresent delivery status</li>
<li>Circumvent the platform for fraudulent activities</li>
<li>Violate transportation or customs regulations</li>
</ul>
<p>Violations may result in shipment cancellation, account suspension, permanent account termination, and legal action where applicable.</p>`,
    icon: "warning",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary:
      "For Logistics Support, contact support@trocanet.com. Business hours: Monday–Saturday, 9:00 AM–7:00 PM IST.",
    body: `<p><strong>Logistics Support</strong></p>
<p><a href="mailto:support@trocanet.com">support@trocanet.com</a></p>
<p><strong>Business Hours</strong><br />Monday – Saturday<br />9:00 AM – 7:00 PM IST</p>
<p><a href="/contact">Contact Support</a></p>`,
    icon: "envelope",
  },
];

const SECTION_ICON_HINTS = [
  { match: /logistic|freight|shipment|transport|warehouse|delivery|cargo|truck/i, icon: "truck" },
  { match: /legal|lawyer|justice|law firm|dispute|governance/i, icon: "gavel" },
  { match: /consult|advisory|engagement|briefcase/i, icon: "user" },
  { match: /quality|inspect|audit|certif|compliance|laboratory/i, icon: "shield" },
  { match: /procure|p2p|requisition|invoice|workflow/i, icon: "desktop" },
  { match: /eligib|registr|account|user|kyc|verif|responsib|professional/i, icon: "user" },
  { match: /auction|bid|gavel|rules/i, icon: "gavel" },
  { match: /manufactur|factory|oem|odm|production/i, icon: "industry" },
  { match: /obligat|conduct|matching|communication|confidential|report|intellectual/i, icon: "lock" },
  { match: /service|platform|feature|listing|requirement|assessment/i, icon: "desktop" },
  { match: /payment|fee|pricing|billing|settlement|order|financial|wallet|freight charge/i, icon: "card" },
  { match: /prohibit|restrict|forbidden|ban|counterfeit|fraud|manipulat/i, icon: "ban" },
  { match: /liabilit|disclaim|indemn|warning/i, icon: "warning" },
  { match: /change|amend|update|modif/i, icon: "lock" },
  { match: /contact|support|help/i, icon: "envelope" },
  { match: /intro|general|overview|purpose/i, icon: "file" },
];

function slugify(value, fallback) {
  const slug = String(value || "")
    .toLowerCase()
    .replace(/&amp;/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || fallback;
}

function stripTags(html) {
  return String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function guessIcon(title, index) {
  for (const hint of SECTION_ICON_HINTS) {
    if (hint.match.test(title)) return hint.icon;
  }
  const fallback = [
    "file",
    "user",
    "gavel",
    "industry",
    "shield",
    "desktop",
    "card",
    "ban",
    "warning",
    "lock",
    "envelope",
  ];
  return fallback[index % fallback.length];
}

function getThinContentDefaults(moduleName) {
  if (moduleName === "PLATFORM") return DEFAULT_PLATFORM_SECTIONS.map((s) => ({ ...s }));
  if (moduleName === "AUCTION") return DEFAULT_AUCTION_SECTIONS.map((s) => ({ ...s }));
  if (moduleName === "INFRASTRUCTURE_LEASING") {
    return DEFAULT_INFRASTRUCTURE_LEASING_SECTIONS.map((s) => ({ ...s }));
  }
  if (moduleName === "CONTRACT_MANUFACTURING") {
    return DEFAULT_CONTRACT_MANUFACTURING_SECTIONS.map((s) => ({ ...s }));
  }
  if (moduleName === "PROCURE_TO_PAY") {
    return DEFAULT_PROCURE_TO_PAY_SECTIONS.map((s) => ({ ...s }));
  }
  if (moduleName === "QUALITY_ASSESSMENT") {
    return DEFAULT_QUALITY_ASSESSMENT_SECTIONS.map((s) => ({ ...s }));
  }
  if (moduleName === "CONSULTANTS") {
    return DEFAULT_CONSULTANTS_SECTIONS.map((s) => ({ ...s }));
  }
  if (moduleName === "LEGAL") {
    return DEFAULT_LEGAL_SECTIONS.map((s) => ({ ...s }));
  }
  if (moduleName === "LOGISTICS") {
    return DEFAULT_LOGISTICS_SECTIONS.map((s) => ({ ...s }));
  }
  return null;
}

/**
 * Split CMS HTML on h2/h3 headings into accordion sections.
 * Falls back to module defaults when content is too thin to structure.
 */
export function parseTermsSectionsFromHtml(html, { moduleName = "PLATFORM" } = {}) {
  const content = String(html || "").trim();
  if (!content) {
    return getThinContentDefaults(moduleName) || [];
  }

  const headingRegex = /<h([23])[^>]*>([\s\S]*?)<\/h\1>/gi;
  const matches = [...content.matchAll(headingRegex)];

  if (!matches.length) {
    const defaults = getThinContentDefaults(moduleName);
    if (defaults && stripTags(content).length < 280) return defaults;
    const summary = stripTags(content).slice(0, 180);
    return [
      {
        id: "terms-content",
        title: "Terms & Conditions",
        summary: summary || "Review the terms and conditions for this module.",
        body: content,
        icon: "file",
      },
    ];
  }

  const sections = matches.map((match, index) => {
    const title = stripTags(match[2]) || `Section ${index + 1}`;
    const start = match.index + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index : content.length;
    const body = content.slice(start, end).trim() || `<p>${title}</p>`;
    const summary = stripTags(body).slice(0, 180) || title;
    return {
      id: slugify(title, `section-${index + 1}`),
      title,
      summary,
      body,
      icon: guessIcon(title, index),
    };
  });

  // Seeded content is usually a single short h2 — show the designed module accordion.
  if (sections.length <= 1) {
    const defaults = getThinContentDefaults(moduleName);
    if (defaults && stripTags(content).length < 280) {
      return defaults;
    }
  }

  return sections;
}

export function formatTermsVersionLabel(version) {
  const n = Number(version);
  if (!Number.isFinite(n) || n <= 0) return "v1.0";
  return Number.isInteger(n) ? `v${n}.0` : `v${n}`;
}
